/**
 * Per-tool renderers. Each receives assistant-ui's ToolCallMessagePartProps;
 * `artifact` carries the reducer's ToolPart plus any permission request.
 */

import type { ToolCallMessagePartProps } from '@assistant-ui/react'
import type { ToolArtifact } from '@web/adapter/toThreadMessages'
import { useOptionalBridge } from '@web/adapter/useBridgeRuntime'
import { Markdown } from '@web/components/Markdown'
import { NestedTranscript } from '@web/components/NestedTranscript'
import {
  isDataObject,
  readableResult,
  StructuredValue,
  TechnicalDetails,
} from '@web/components/tools/StructuredContent'
import {
  OutputBlock,
  resultText,
  ToolShell,
} from '@web/components/tools/ToolShell'
import { collapseContext, lineDiff } from '@web/lib/diff'
import { shortPath, truncate } from '@web/lib/format'
import { toolDisplayName } from '@web/lib/toolNames'
import { terminalText } from '@web/state/acp'

type Props = ToolCallMessagePartProps<Record<string, unknown>, unknown>
const art = (p: Props) => p.artifact as ToolArtifact | undefined

// ---------------------------------------------------------------- Bash

export function BashTool(p: Props) {
  const a = art(p)
  const command = p.args.command ?? p.args.cmd ?? ''
  const cmd = Array.isArray(command) ? command.join(' ') : String(command)
  const desc = p.args.description ? String(p.args.description) : null
  const output = readableResult(p.result)
  return (
    <ToolShell
      icon="terminal"
      name={a?.title ?? 'Run command'}
      summary={desc ?? truncate(cmd, 120)}
      artifact={a}
      result={p.result}
      isError={p.isError}
    >
      <pre className="mono whitespace-pre-wrap break-words rounded-control border border-line bg-inset px-3 py-2 text-[12px] text-fg">
        <span className="select-none text-faint">$ </span>
        {cmd}
      </pre>
      {p.args.run_in_background ? (
        <div className="mt-1 text-[11px] text-faint">
          Runs in the background.
        </div>
      ) : null}
      {typeof output === 'string' ? (
        <OutputBlock text={output} className="mt-2" />
      ) : (
        <ReadableOutput result={p.result} />
      )}
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

// ---------------------------------------------------------------- Read

export function ReadTool(p: Props) {
  const a = art(p)
  const file = String(p.args.file_path ?? p.args.path ?? firstLocation(a))
  const range =
    p.args.offset != null
      ? ` (from line ${String(p.args.offset)}${p.args.limit != null ? `, ${String(p.args.limit)} lines` : ''})`
      : ''
  return (
    <ToolShell
      icon="fileText"
      name={a?.title ?? 'Read file'}
      summary={shortPath(file) + range}
      artifact={a}
      result={p.result}
      isError={p.isError}
    >
      {file && (
        <div className="mb-2 break-all text-[11px] text-muted">{file}</div>
      )}
      <ReadableOutput result={p.result} />
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

// ---------------------------------------------------------------- Edit / MultiEdit

type EditArgs = {
  file_path?: string
  old_string?: string
  new_string?: string
  replace_all?: boolean
  edits?: { old_string: string; new_string: string }[]
}

export function EditTool(p: Props) {
  const a = art(p)
  const args = p.args as EditArgs
  const file = String(args.file_path ?? p.args.path ?? firstLocation(a))
  const edits =
    args.edits ??
    (args.old_string !== undefined || args.new_string !== undefined
      ? [
          {
            old_string: args.old_string ?? '',
            new_string: args.new_string ?? '',
          },
        ]
      : [])
  const stats = edits.reduce(
    (acc, e) => {
      const d = lineDiff(e.old_string, e.new_string)
      acc.add += d.filter((l) => l.kind === 'add').length
      acc.del += d.filter((l) => l.kind === 'del').length
      return acc
    },
    { add: 0, del: 0 },
  )
  return (
    <ToolShell
      icon="edit"
      name={a?.title ?? 'Edit file'}
      summary={shortPath(file)}
      artifact={a}
      result={p.result}
      isError={p.isError}
      headerExtra={
        edits.length > 0 && (
          <span className="mono shrink-0 text-[11px]">
            <span className="text-ok">+{stats.add}</span>{' '}
            <span className="text-danger">−{stats.del}</span>
          </span>
        )
      }
    >
      {edits.map((e, i) => (
        <DiffView key={i} oldText={e.old_string} newText={e.new_string} />
      ))}
      {!p.artifact || (a && a.part.argsSettled) ? null : (
        <div className="text-[11px] text-faint">Receiving edit…</div>
      )}
      {p.isError && (
        <OutputBlock text={resultText(p.result)} className="mt-2" />
      )}
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

export function DiffView({
  oldText,
  newText,
}: {
  oldText: string
  newText: string
}) {
  const lines = collapseContext(lineDiff(oldText, newText))
  return (
    <pre className="mono my-1 max-h-[60vh] overflow-auto rounded-control border border-line bg-inset text-[12px] leading-5">
      {lines.map((l, i) => (
        <div
          key={i}
          className={`px-3 ${
            l.kind === 'add'
              ? 'bg-[var(--diff-add-bg)] text-[var(--diff-add-fg)]'
              : l.kind === 'del'
                ? 'bg-[var(--diff-del-bg)] text-[var(--diff-del-fg)]'
                : 'text-muted'
          }`}
        >
          <span className="mr-2 inline-block w-3 select-none text-faint">
            {l.kind === 'add' ? '+' : l.kind === 'del' ? '−' : ' '}
          </span>
          {l.text || ' '}
        </div>
      ))}
    </pre>
  )
}

// ---------------------------------------------------------------- Write

export function WriteTool(p: Props) {
  const a = art(p)
  const file = String(p.args.file_path ?? p.args.path ?? firstLocation(a))
  const content = String(p.args.content ?? '')
  const n = content ? content.split('\n').length : 0
  return (
    <ToolShell
      icon="write"
      name={a?.title ?? 'Write file'}
      summary={shortPath(file)}
      artifact={a}
      result={p.result}
      isError={p.isError}
      headerExtra={<span className="mono text-[11px] text-ok">{n} lines</span>}
    >
      <OutputBlock text={content} maxLines={30} />
      {p.isError && (
        <OutputBlock text={resultText(p.result)} className="mt-2" />
      )}
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

// ---------------------------------------------------------------- Grep / Glob

export function SearchTool(p: Props) {
  const a = art(p)
  const pattern = String(p.args.pattern ?? p.args.query ?? '')
  const where = p.args.path ? ` in ${shortPath(String(p.args.path))}` : ''
  const out = resultText(p.result)
  const count = out ? out.split('\n').filter(Boolean).length : 0
  return (
    <ToolShell
      icon="search"
      name={a?.title ?? 'Search'}
      summary={`${pattern}${where}`}
      artifact={a}
      result={p.result}
      isError={p.isError}
      headerExtra={
        p.result !== undefined ? <span className="chip">{count}</span> : null
      }
    >
      <ReadableOutput result={p.result} />
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

// ---------------------------------------------------------------- Agent / Task (subagents)

export function AgentTool(p: Props) {
  const a = art(p)
  const desc = String(p.args.description ?? p.args.prompt ?? '')
  const kind = p.args.subagent_type
    ? String(p.args.subagent_type)
    : a?.part.progress?.subagentType
  const nested = p.messages ?? []
  return (
    <ToolShell
      icon="agent"
      name={a?.title ?? (kind ? `Agent · ${kind}` : 'Delegate task')}
      summary={truncate(desc, 120)}
      artifact={a}
      result={p.result}
      isError={p.isError}
      headerExtra={
        nested.length ? (
          <span className="chip">{nested.length} msgs</span>
        ) : null
      }
    >
      {p.args.prompt ? (
        <details className="mb-2 text-xs">
          <summary className="cursor-pointer text-faint">Prompt</summary>
          <Markdown
            text={String(p.args.prompt)}
            className="mt-1 text-[12.5px]"
          />
        </details>
      ) : null}
      {nested.length > 0 && <NestedTranscript messages={nested} />}
      {p.result !== undefined && (
        <div className="mt-2">
          <div className="label mb-1">Result</div>
          <ReadableOutput result={p.result} />
        </div>
      )}
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

// ---------------------------------------------------------------- Todo / Task list

type Todo = {
  content?: string
  subject?: string
  description?: string
  status?: string
  activeForm?: string
}

export function TodoTool(p: Props) {
  const a = art(p)
  const todos = (p.args.todos as Todo[] | undefined) ?? []
  const single =
    !todos.length && (p.args.subject || p.args.content) ? [p.args as Todo] : []
  const items = todos.length ? todos : single
  const done = items.filter((t) => t.status === 'completed').length
  return (
    <ToolShell
      icon="todo"
      name={a?.title ?? 'Update tasks'}
      summary={
        items.length
          ? `${done}/${items.length} done`
          : truncate(String(p.args.subject ?? p.args.description ?? ''), 100)
      }
      artifact={a}
      result={p.result}
      isError={p.isError}
    >
      <ul className="space-y-1 text-[13px]">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2">
            <span
              className={`mt-0.5 inline-block h-3.5 w-3.5 shrink-0 rounded-full border ${t.status === 'completed' ? 'border-ok bg-ok' : t.status === 'in_progress' ? 'border-accent' : 'border-line-strong'}`}
            />
            <span
              className={
                t.status === 'completed' ? 'text-faint line-through' : ''
              }
            >
              {t.content ?? t.subject ?? t.description}
            </span>
          </li>
        ))}
      </ul>
      {!items.length && <StructuredValue value={p.args} />}
      {p.result !== undefined && (
        <div className="mt-2">
          <ReadableOutput result={p.result} />
        </div>
      )}
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

// ---------------------------------------------------------------- Web

export function WebTool(p: Props) {
  const a = art(p)
  const target = String(p.args.url ?? p.args.query ?? '')
  return (
    <ToolShell
      icon="web"
      name={a?.title ?? 'Browse web'}
      summary={target}
      artifact={a}
      result={p.result}
      isError={p.isError}
    >
      {target && (
        <div className="mb-2 text-[12px]">
          <StructuredValue value={target} />
        </div>
      )}
      {p.args.prompt ? (
        <div className="mb-2 text-xs text-muted">{String(p.args.prompt)}</div>
      ) : null}
      <ReadableOutput result={p.result} />
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} />
    </ToolShell>
  )
}

// ---------------------------------------------------------------- Fallback

export function GenericTool(p: Props) {
  const a = art(p)
  const summary = firstStringArg(p.args)
  const rawMcpName = p.toolName?.startsWith('mcp__') ? p.toolName : undefined
  return (
    <ToolShell
      icon="tool"
      name={toolDisplayName({
        identifier: p.toolName,
        title: a?.title,
        presentation: a?.presentation,
      })}
      summary={summary}
      artifact={a}
      result={p.result}
      isError={p.isError}
    >
      {Object.keys(p.args).length > 0 && (
        <>
          <div className="label mb-1">Input</div>
          <StructuredValue value={p.args} />
        </>
      )}
      {p.result !== undefined && (
        <>
          <div className="label mb-1 mt-2">Output</div>
          <ReadableOutput result={p.result} />
        </>
      )}
      <ToolContent artifact={a} />
      <TechnicalDetails args={p.args} result={p.result} toolName={rawMcpName} />
    </ToolShell>
  )
}

export function firstStringArg(args: Record<string, unknown>): string {
  for (const k of [
    'description',
    'command',
    'file_path',
    'path',
    'pattern',
    'query',
    'url',
    'name',
    'prompt',
  ]) {
    const v = args[k]
    if (typeof v === 'string' && v) return truncate(v, 120)
  }
  const v = Object.values(args).find((x) => typeof x === 'string')
  return typeof v === 'string' ? truncate(v, 120) : ''
}

export function ReadableOutput({ result }: { result: unknown }) {
  if (result === undefined) return null
  const value = readableResult(result)
  if (value === '' || value === null || value === undefined)
    return <p className="text-[12px] text-muted">No output</p>
  return (
    <div className="text-[12.5px]">
      <StructuredValue value={value} />
    </div>
  )
}

function firstLocation(artifact?: ToolArtifact): string {
  const locations = artifact?.locations
  return Array.isArray(locations) &&
    isDataObject(locations[0]) &&
    typeof locations[0].path === 'string'
    ? locations[0].path
    : ''
}

/** ACP content carries typed diffs and resources separately from raw output. */
export function ToolContent({ artifact }: { artifact?: ToolArtifact }) {
  const entries = Array.isArray(artifact?.content) ? artifact.content : []
  const partial =
    artifact &&
    ['pending', 'running'].includes(artifact.status ?? '') &&
    entries.length === 0 &&
    artifact.output !== undefined
  return (
    <>
      {partial && (
        <div className="mt-2 text-[12.5px]" data-tool-progress>
          <StructuredValue value={readableResult(artifact.output)} />
        </div>
      )}
      {entries.map((entry, index) => {
        if (!isDataObject(entry)) return null
        if (entry.type === 'diff')
          return (
            <div key={index} className="mt-2 text-xs">
              {Array.isArray(entry.changes) &&
                entry.changes.map((change, changeIndex) => (
                  <div key={changeIndex} className="mb-1 break-all text-muted">
                    <StructuredValue value={change} />
                  </div>
                ))}
              {isDataObject(entry.patch) &&
                typeof entry.patch.text === 'string' && (
                  <OutputBlock text={entry.patch.text} maxLines={80} />
                )}
            </div>
          )
        if (entry.type === 'terminal')
          return (
            <TerminalContent
              key={index}
              terminalId={String(entry.terminalId ?? '')}
            />
          )
        const content = isDataObject(entry.content) ? entry.content : entry
        if (content.type === 'text' && typeof content.text === 'string')
          return (
            <Markdown
              key={index}
              text={content.text}
              className="mt-2 text-[12.5px]"
            />
          )
        if (content.type === 'resource_link')
          return (
            <div key={index} className="mt-2 text-[12px]">
              <span className="font-medium">
                {String(content.name ?? 'Resource')}
              </span>
              <div>
                <StructuredValue value={content.uri} />
              </div>
            </div>
          )
        if (content.type === 'resource' && isDataObject(content.resource))
          return (
            <div key={index} className="mt-2">
              <StructuredValue value={content.resource} />
            </div>
          )
        if (
          content.type === 'image' &&
          typeof content.data === 'string' &&
          typeof content.mimeType === 'string' &&
          /^image\/(png|jpeg|gif|webp)$/.test(content.mimeType)
        )
          return (
            <img
              key={index}
              className="my-2 max-h-64 max-w-full rounded-control"
              src={`data:${content.mimeType};base64,${content.data}`}
              alt="Tool result"
            />
          )
        return null
      })}
    </>
  )
}

/** Terminal references show agent-owned output; they cannot execute commands. */
function TerminalContent({ terminalId }: { terminalId: string }) {
  const bridge = useOptionalBridge()
  const terminal = bridge?.snapshot.acp.terminals[terminalId]
  return (
    <div className="mt-2 text-xs">
      <p className="text-muted">
        {terminal?.command ?? `Terminal ${terminalId}`}
      </p>
      {terminal && <OutputBlock text={terminalText(terminal)} maxLines={60} />}
    </div>
  )
}
